import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Users,
  Calendar,
  Download,
  Printer,
  AlertTriangle,
  Mail,
  Phone,
  FileText,
  Clock,
  Baby,
  Utensils,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';

export function ManifestPage() {
  const { t } = useTranslation('operations');
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error } = trpc.package.getDepartureManifest.useQuery(id || '', {
    enabled: !!id,
  });

  const exportToCSV = () => {
    if (!data) return;

    const headers = [
      t('csv.bookingRef'),
      t('csv.firstName'),
      t('csv.lastName'),
      t('csv.type'),
      t('csv.lead'),
      t('csv.dob'),
      t('csv.nationality'),
      t('csv.passportNo'),
      t('csv.passportExpiry'),
      t('csv.mealPref'),
      t('csv.specialNeeds'),
      t('csv.customerEmail'),
      t('csv.customerPhone'),
    ];

    const rows = data.allPassengers.map((p) => [
      p.bookingReference,
      p.firstName,
      p.lastName,
      p.passengerType,
      p.isLeadPassenger ? 'Yes' : 'No',
      p.dateOfBirth ? new Date(p.dateOfBirth).toLocaleDateString() : '',
      p.nationality || '',
      p.passportNumber || '',
      p.passportExpiry ? new Date(p.passportExpiry).toLocaleDateString() : '',
      p.mealPreference || '',
      p.specialNeeds || '',
      p.customer.email,
      p.customer.phone || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `manifest-${data.package.code}-${new Date(data.departure.departureDate).toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <Users className="mx-auto h-8 w-8 animate-pulse text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">{t('loadingManifest', 'Loading manifest...')}</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-4 text-lg font-medium">{t('error', 'Error loading manifest')}</h3>
          <Link to="/travel/operations/calendar">
            <Button className="mt-4 h-8">{t('backToCalendar', 'Back to Calendar')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <div className="flex items-center gap-4">
          <Link to="/travel/operations/calendar">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{t('manifest', 'Departure Manifest')}</h1>
            <p className="text-muted-foreground">{data.package.name}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-8" onClick={exportToCSV}>
            <Download className="mr-2 h-4 w-4" />
            {t('exportCSV', 'Export CSV')}
          </Button>
          <Button variant="outline" className="h-8" onClick={handlePrint}>
            <Printer className="mr-2 h-4 w-4" />
            {t('print', 'Print')}
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block">
        <h1 className="text-xl font-bold">{data.package.name} - {t('passengerManifest')}</h1>
        <p className="text-sm text-gray-600">
          {new Date(data.departure.departureDate).toLocaleDateString()} -{' '}
          {new Date(data.departure.returnDate).toLocaleDateString()}
        </p>
      </div>

      {/* Departure Info */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 print:grid-cols-4">
        <div className="border border-border bg-white p-4 print:p-2 print:border-gray-300">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center bg-primary/10 print:h-6 print:w-6">
              <Calendar className="h-4 w-4 text-primary print:h-3 print:w-3" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('departureDate', 'Departure')}</p>
              <p className="font-semibold">
                {new Date(data.departure.departureDate).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        <div className="border border-border bg-white p-4 print:p-2 print:border-gray-300">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center bg-blue-100 print:h-6 print:w-6">
              <Clock className="h-4 w-4 text-blue-600 print:h-3 print:w-3" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('duration', 'Duration')}</p>
              <p className="font-semibold">{data.package.duration} {t('days', 'days')}</p>
            </div>
          </div>
        </div>

        <div className="border border-border bg-white p-4 print:p-2 print:border-gray-300">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center bg-green-100 print:h-6 print:w-6">
              <Users className="h-4 w-4 text-green-600 print:h-3 print:w-3" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('passengers', 'Passengers')}</p>
              <p className="font-semibold">
                {data.stats.totalPassengers} / {data.stats.capacity}
              </p>
            </div>
          </div>
        </div>

        <div className="border border-border bg-white p-4 print:p-2 print:border-gray-300">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center bg-purple-100 print:h-6 print:w-6">
              <FileText className="h-4 w-4 text-purple-600 print:h-3 print:w-3" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('bookings', 'Bookings')}</p>
              <p className="font-semibold">{data.stats.totalBookings}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="border border-border bg-white p-6 print:p-3 print:border-gray-300">
        <h2 className="mb-4 font-semibold print:text-sm">{t('summary', 'Summary')}</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5 print:grid-cols-5">
          <div className="text-center">
            <p className="text-3xl font-bold text-primary print:text-lg">{data.stats.adults}</p>
            <p className="text-sm text-muted-foreground print:text-xs">{t('adults', 'Adults')}</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600 print:text-lg">{data.stats.children}</p>
            <p className="text-sm text-muted-foreground print:text-xs">{t('children', 'Children')}</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600 print:text-lg">{data.stats.infants}</p>
            <p className="text-sm text-muted-foreground print:text-xs">{t('infants', 'Infants')}</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600 print:text-lg">{data.stats.availableSpots}</p>
            <p className="text-sm text-muted-foreground print:text-xs">{t('available', 'Available')}</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-orange-600 print:text-lg">{data.stats.fillRate}%</p>
            <p className="text-sm text-muted-foreground print:text-xs">{t('fillRate', 'Fill Rate')}</p>
          </div>
        </div>
      </div>

      {/* Document Alerts */}
      {data.documentAlerts.length > 0 && (
        <div className="border border-red-200 bg-red-50 p-4 print:p-3 print:border-red-300">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            <h3 className="font-semibold">{t('documentAlerts', 'Document Alerts')}</h3>
          </div>
          <ul className="mt-3 space-y-2">
            {data.documentAlerts.map((alert) => (
              <li
                key={alert.passengerId}
                className="flex items-center justify-between text-sm"
              >
                <span>
                  <span className="font-medium">{alert.passengerName}</span>
                  <span className="text-red-600 ml-2">
                    ({alert.isExpired ? t('expired', 'Expired') : t('expiringSoon', 'Expiring Soon')})
                  </span>
                </span>
                <span className="text-muted-foreground">
                  {t('passport', 'Passport')}:{' '}
                  {alert.passportExpiry
                    ? new Date(alert.passportExpiry).toLocaleDateString()
                    : 'N/A'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Passenger List by Booking */}
      <div className="border border-border bg-white print:border-gray-300">
        <div className="border-b p-4 print:p-2">
          <h2 className="font-semibold">{t('passengerList', 'Passenger List')}</h2>
        </div>

        <div className="divide-y print:text-sm">
          {data.bookings.map((booking) => (
            <div key={booking.id} className="p-4 print:p-2">
              {/* Booking Header */}
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 bg-gray-50 -mx-4 -mt-4 px-4 py-2 print:-mx-2 print:-mt-2 print:px-2">
                <div className="flex items-center gap-4">
                  <span className="font-mono font-semibold">{booking.bookingReference}</span>
                  <span className="text-sm text-muted-foreground">
                    {t('passengerCount', { count: booking.passengerCount })}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {booking.customer.email}
                  </span>
                  {booking.customer.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {booking.customer.phone}
                    </span>
                  )}
                </div>
              </div>

              {/* Passengers Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">{t('name', 'Name')}</th>
                      <th className="pb-2 pr-4 font-medium">{t('type', 'Type')}</th>
                      <th className="pb-2 pr-4 font-medium print:hidden">{t('nationality', 'Nationality')}</th>
                      <th className="pb-2 pr-4 font-medium">{t('passport', 'Passport')}</th>
                      <th className="pb-2 pr-4 font-medium print:hidden">{t('dob', 'DOB')}</th>
                      <th className="pb-2 font-medium print:hidden">{t('notes', 'Notes')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {booking.passengers.map((passenger: typeof booking.passengers[number]) => (
                      <tr key={passenger.id}>
                        <td className="py-2 pr-4">
                          <div className="flex items-center gap-2">
                            {passenger.isLeadPassenger && (
                              <span className="flex h-5 w-5 items-center justify-center bg-primary text-[10px] font-bold text-white">
                                L
                              </span>
                            )}
                            <span className="font-medium">
                              {passenger.firstName} {passenger.lastName}
                            </span>
                          </div>
                        </td>
                        <td className="py-2 pr-4">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border ${
                              passenger.passengerType === 'ADULT'
                                ? 'bg-blue-100 text-blue-700 border-blue-200'
                                : passenger.passengerType === 'CHILD'
                                ? 'bg-green-100 text-green-700 border-green-200'
                                : 'bg-purple-100 text-purple-700 border-purple-200'
                            }`}
                          >
                            {passenger.passengerType === 'INFANT' && (
                              <Baby className="h-3 w-3" />
                            )}
                            {passenger.passengerType}
                          </span>
                        </td>
                        <td className="py-2 pr-4 print:hidden">
                          {passenger.nationality || '-'}
                        </td>
                        <td className="py-2 pr-4">
                          {passenger.passportNumber || '-'}
                          {passenger.passportExpiry && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              (exp: {new Date(passenger.passportExpiry).toLocaleDateString()})
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-4 print:hidden">
                          {passenger.dateOfBirth
                            ? new Date(passenger.dateOfBirth).toLocaleDateString()
                            : '-'}
                        </td>
                        <td className="py-2 print:hidden">
                          <div className="flex flex-wrap gap-1">
                            {passenger.mealPreference && (
                              <span className="inline-flex items-center gap-1 bg-orange-100 px-1.5 py-0.5 text-xs text-orange-700 border border-orange-200">
                                <Utensils className="h-3 w-3" />
                                {passenger.mealPreference}
                              </span>
                            )}
                            {passenger.specialNeeds && (
                              <span className="inline-flex items-center gap-1 bg-red-100 px-1.5 py-0.5 text-xs text-red-700 border border-red-200">
                                <AlertCircle className="h-3 w-3" />
                                {passenger.specialNeeds}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {data.bookings.length === 0 && (
            <div className="p-8 text-center">
              <Users className="mx-auto h-12 w-12 text-muted-foreground/30" />
              <p className="mt-4 text-muted-foreground">{t('noPassengers', 'No passengers yet')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      {data.departure.notes && (
        <div className="border border-border bg-white p-4 print:p-3 print:border-gray-300">
          <h3 className="font-semibold mb-2">{t('departureNotes', 'Departure Notes')}</h3>
          <p className="text-muted-foreground">{data.departure.notes}</p>
        </div>
      )}

      {/* Print Footer */}
      <div className="hidden print:block text-center text-xs text-gray-500 mt-8 pt-4 border-t">
        <p>{t('printFooter', { date: new Date().toLocaleString(), company: `${data.package.code} | Polatkan Travel Agency` })}</p>
      </div>
    </div>
  );
}
